import React, { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import {
  FaNetworkWired,
  FaDesktop,
  FaLaptop,
  FaServer,
  FaVideo,
  FaCamera,
  FaTools,
  FaCog,
  FaQuoteLeft,
  FaQuoteRight,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
} from "react-icons/fa";

// Intersection Observer hook for reveal-on-visible animation
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

const testimonials = [
  {
    icon: <FaNetworkWired className="text-blue-700 text-2xl" />,
    title: "NETWORKING AMC",
    name: "Ankit Sharma",
    feedback:
      "STRALIA PRIVATE LIMITED keeps our network running 24/7! Fast response, professional staff, and zero downtime for our business. Highly recommended for Networking AMC.",
  },
  {
    icon: <FaDesktop className="text-green-600 text-2xl" />,
    title: "COMPUTER AMC",
    name: "Megha Singh",
    feedback:
      "Our office computers have never worked better. Year-round Computer AMC from STRALIA is worry-free and dependable. Excellent troubleshooting and support.",
  },
  {
    icon: <FaLaptop className="text-indigo-600 text-2xl" />,
    title: "LAPTOP AMC",
    name: "Rajiv Malhotra",
    feedback:
      "The laptop AMC service keeps our field team productive. Timely software updates, hardware checks, and friendly engineers. STRALIA always delivers.",
  },
  {
    icon: <FaServer className="text-purple-600 text-2xl" />,
    title: "SERVER AMC",
    name: "Sonal Verma",
    feedback:
      "Downtime was a big issue before STRALIA’s server AMC. Now, our data and systems are secure and supported with expert care. Impressed with their professionalism.",
  },
  {
    icon: <FaVideo className="text-yellow-600 text-2xl" />,
    title: "PROJECTOR AMC",
    name: "Sunil Kumar",
    feedback:
      "From blurry projection to crystal-clear images! Their Projector AMC ensures our boardrooms are always ready. Skilled and punctual team.",
  },
  {
    icon: <FaCamera className="text-red-500 text-2xl" />,
    title: "CCTV AMC",
    name: "Priya Nair",
    feedback:
      "Complete CCTV AMC care: cleaning, updating, and quick fixes. STRALIA provides complete peace of mind for our security needs.",
  },
  {
    icon: <FaTools className="text-orange-500 text-2xl" />,
    title: "CCTV INSTALLATION & SERVICE",
    name: "Akash Tiwari",
    feedback:
      "Super efficient! STRALIA handled our CCTV installation, setup, and app integration. The support team is always just a call away.",
  },
  {
    icon: <FaCog className="text-gray-800 text-2xl" />,
    title: "ALL IT SERVICES",
    name: "Nisha Gupta",
    feedback:
      "One-stop solution for all IT issues! STRALIA manages everything: upgrades, consulting, and day-to-day IT support. More than satisfied.",
  },
];

const contactInfo = [
  {
    icon: <FaPhoneAlt className="text-blue-600 mr-2" />,
    label: "Phone",
    value: (
      <>
        <a href="tel:9217208800" className="hover:underline">9217208800</a>,{" "}
        <a href="tel:9217209900" className="hover:underline">9217209900</a>,{" "}
        <a href="tel:9217204400" className="hover:underline">9217204400</a>
      </>
    ),
  },
  {
    icon: <FaEnvelope className="text-blue-600 mr-2" />,
    label: "Email",
    value: (
      <a href="mailto:straliapvtltd@gmail.com" className="hover:underline">
        straliapvtltd@gmail.com
      </a>
    ),
  },
  {
    icon: <FaMapMarkerAlt className="text-blue-600 mr-2" />,
    label: "Address",
    value: (
      <>
        office no. 179-A FIRST FLOOR (B/P),<br />JIVAN NAGAR, ASHRAM, SOUTH EAST DELHI - 110014
      </>
    ),
  },
];

export default function Testimonials() {
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Allow animation trigger when 60px of section is visible
  const isVisible = useOnScreen(sectionRef, "-60px");

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  // Animation utility classes
  const animateClass = hasAnimated
    ? "opacity-100 translate-y-0"
    : "opacity-0 translate-y-12";
  const transitionClass = "transition-all duration-900 ease-out";

  return (
    <section
      ref={sectionRef}
      className={`relative py-28 bg-gradient-to-br from-blue-50 to-white overflow-hidden font-PTSerif-Regular
        ${animateClass} ${transitionClass}`}
    >
      {/* Decorative BG */}
      <div className="absolute inset-0 bg-white/70 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 font-PTSerif-Regular">
        {/* Heading */}
        <div className="text-center mb-12 font-PTSerif-Regular">
          <h2 className="text-5xl font-extrabold text-blue-900 tracking-tight font-PTSerif-Regular">
            What Our Clients Say
          </h2>
          <div className="w-20 h-1 bg-blue-700 mx-auto my-5 rounded" />
          <p className="text-base text-gray-700 max-w-xl mx-auto font-PTSerif-Regular">
            STRALIA PRIVATE LIMITED brings reliable IT, AMC, and installation services for all industries in Delhi NCR.
          </p>
        </div>
        {/* Swiper Section */}
        <Swiper
          modules={[Navigation, Pagination, Autoplay, A11y]}
          slidesPerView={1}
          loop={true}
          spaceBetween={36}
          navigation
          pagination={{ clickable: true }}
          autoplay={{ delay: 4600 }}
          className="!pb-8 font-PTSerif-Regular"
        >
          {testimonials.map((t, idx) => (
            <SwiperSlide key={idx}>
              <div className="bg-white rounded-3xl shadow-2xl max-w-3xl mx-auto px-8 py-10 relative flex flex-col items-center text-center border border-blue-100 font-PTSerif-Regular">
                <span className="absolute left-7 top-5 opacity-20 text-5xl text-blue-300 z-0">
                  <FaQuoteLeft />
                </span>
                <span className="absolute right-7 bottom-5 opacity-20 text-5xl text-blue-300 z-0">
                  <FaQuoteRight />
                </span>
                <div className="mb-5 flex items-center gap-3 z-10 font-PTSerif-Regular">
                  {t.icon}
                  <span className="font-bold uppercase tracking-wider text-blue-800 text-sm font-PTSerif-Regular">{t.title}</span>
                </div>
                <p className="text-gray-800 text-lg font-medium z-10 font-PTSerif-Regular">{t.feedback}</p>
                <div className="mt-7 flex flex-col items-center z-10 font-PTSerif-Regular">
                  <div className="font-semibold text-blue-700 text-base font-PTSerif-Regular">{t.name}</div>
                  <div className="opacity-60 text-xs mt-1 font-PTSerif-Regular">Client</div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}